import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Typography,
    Grid,
    Paper,
    TextField,
    Button,
    Avatar,
    Divider,
    Stack,
    InputAdornment,
    IconButton,
    Snackbar,
    Alert,
    CircularProgress,
    Chip
} from '@mui/material';
import { Person, Lock, Visibility, VisibilityOff, Badge, Email, Security, Save, AccountBalance } from '@mui/icons-material';
import BankAccountManager from 'views/common/bank-accounts/BankAccountManager';
import { useMe } from 'hooks/useUser';

export default function AdminProfile() {
    const customization = useSelector((state) => state.customization);
    const userInfo = customization?.userInfo || JSON.parse(localStorage.getItem('userInfo')) || {};

    // 1. Data Hook
    const { data: meData, isLoading: loadingProfile } = useMe();
    const adminDetails = meData?.admin;
    const userDetails = meData?.user;

    // 2. Local States
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

    const [adminInfo, setAdminInfo] = useState({
        firstName: userInfo.firstName || '',
        lastName: userInfo.lastName || '',
        email: userInfo.email || '',
        role: userInfo.role || 'Administrator',
        phoneNumber: userInfo.phoneNumber || ''
    });

    useEffect(() => {
        if (userDetails) {
            setAdminInfo({
                firstName: userDetails.firstName || '',
                lastName: userDetails.lastName || '',
                email: userDetails.email || '',
                role: adminDetails?.role?.toUpperCase() || userDetails.role || 'Administrator',
                phoneNumber: userDetails.phoneNumber || ''
            });
        } else if (userInfo) {
            setAdminInfo({
                firstName: userInfo.firstName || '',
                lastName: userInfo.lastName || '',
                email: userInfo.email || '',
                role: userInfo.role || 'Administrator',
                phoneNumber: userInfo.phoneNumber || ''
            });
        }
    }, [userDetails, adminDetails, userInfo]);

    // Password State
    const [passwords, setPasswords] = useState({
        current: '',
        new: '',
        confirm: ''
    });

    // 3. Handlers
    const showAlert = (message, severity = 'success') => {
        setAlert({ open: true, message, severity });
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();

        // Basic Validation
        if (passwords.new !== passwords.confirm) {
            return showAlert('New passwords do not match', 'error');
        }
        if (passwords.new.length < 8) {
            return showAlert('Password must be at least 8 characters', 'error');
        }

        setLoading(true);
        // Simulate API Call
        setTimeout(() => {
            setLoading(false);
            showAlert('Password updated successfully!');
            setPasswords({ current: '', new: '', confirm: '' });
        }, 1500);
    };

    if (loadingProfile) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={4}>
            <Typography variant="h4" fontWeight={800} mb={4}>
                My Account
            </Typography>

            <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert({ ...alert, open: false })}>
                <Alert severity={alert.severity} variant="filled" sx={{ width: '100%' }}>
                    {alert.message}
                </Alert>
            </Snackbar>

            <Grid container spacing={4}>
                {/* COLUMN 1: ACCOUNT INFO */}
                <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 4, borderRadius: 3, height: '100%' }}>
                        <Stack alignItems="center" spacing={2} mb={4}>
                            <Avatar sx={{ width: 100, height: 100, bgcolor: 'primary.main', fontSize: '2rem' }}>
                                {adminInfo.firstName?.[0]}
                                {adminInfo.lastName?.[0]}
                            </Avatar>
                            <Box textAlign="center">
                                <Typography variant="h6">
                                    {adminInfo.firstName} {adminInfo.lastName}
                                </Typography>
                                <Stack direction="row" spacing={1} justifyContent="center" mt={1}>
                                    <Chip label={adminInfo.role} size="small" color="primary" />
                                    <Chip
                                        label={adminDetails?.status?.toUpperCase() || 'ACTIVE'}
                                        size="small"
                                        color={adminDetails?.status === 'active' ? 'success' : 'warning'}
                                        variant="outlined"
                                    />
                                </Stack>
                            </Box>
                        </Stack>

                        <Divider sx={{ mb: 4 }} />

                        <Stack spacing={3}>
                            <TextField
                                fullWidth
                                disabled
                                label="Full Name"
                                value={`${adminInfo.firstName} ${adminInfo.lastName}`}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Badge fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Email Address"
                                value={adminInfo.email}
                                disabled
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <TextField fullWidth label="Phone Number" value={adminInfo.phoneNumber} disabled />

                            {adminDetails?.permissions?.length > 0 && (
                                <Box mt={2}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Assigned Permissions
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {adminDetails.permissions.map((perm) => (
                                            <Chip key={perm} label={perm.replace('_', ' ')} size="small" variant="outlined" />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            <Button variant="contained" startIcon={<Save />} disabled sx={{ mt: 2 }}>
                                Update Account Info
                            </Button>
                            <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                                Account details are managed by the platform administrator.
                            </Typography>
                        </Stack>
                    </Paper>
                </Grid>

                {/* COLUMN 2: SECURITY (CHANGE PASSWORD) */}
                <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 4, borderRadius: 3, height: '100%' }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                            <Security color="primary" />
                            <Typography variant="h6">Security Settings</Typography>
                        </Stack>

                        <Typography variant="body2" color="text.secondary" mb={4}>
                            Ensure your account is using a long, random password to stay secure.
                        </Typography>

                        <Box component="form" onSubmit={handleUpdatePassword}>
                            <Stack spacing={3}>
                                <TextField
                                    fullWidth
                                    type={showPassword ? 'text' : 'password'}
                                    label="Current Password"
                                    value={passwords.current}
                                    onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock fontSize="small" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        )
                                    }}
                                />

                                <Divider>New Password</Divider>

                                <TextField
                                    fullWidth
                                    type="password"
                                    label="New Password"
                                    value={passwords.new}
                                    onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                                />
                                <TextField
                                    fullWidth
                                    type="password"
                                    label="Confirm New Password"
                                    value={passwords.confirm}
                                    onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                                />

                                <Box mt={2}>
                                    <Button type="submit" variant="contained" size="large" fullWidth disabled={loading || !passwords.new}>
                                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
                                    </Button>
                                </Box>
                            </Stack>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* BANK ACCOUNTS */}
            <Box mt={4}>
                <Paper sx={{ p: 4, borderRadius: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                        <AccountBalance color="primary" />
                        <Typography variant="h6">My Bank Accounts</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                        Manage your saved bank accounts. These accounts will be available for selection when requesting a system payout.
                    </Typography>
                    <BankAccountManager />
                </Paper>
            </Box>
        </Box>
    );
}
